import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, ClipboardList, Search } from 'lucide-react';
import { formatMoney, formatQty, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import type { Category } from '../../lib/types';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Badge, Button, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, formatDateTime, useToast } from '../../ui';
import { QtyInput } from '../../ui/inputs';

interface StocktakeRow {
  id: string;
  number: string;
  name: string;
  status: string;
  startedAt: string;
  postedAt: string | null;
  branchName: string;
  varianceValueCents: number | null;
  lineCount: number;
  countedCount: number;
}
interface StocktakeDetail {
  id: string;
  number: string;
  name: string;
  status: string;
  branchName: string;
  startedAt: string;
  postedAt: string | null;
  varianceValueCents: number | null;
  lines: {
    productId: string;
    name: string;
    sku: string;
    unit: string;
    categoryName: string | null;
    expectedMilli: number;
    countedMilli: number | null;
    varianceMilli: number | null;
    varianceValueCents: number | null;
  }[];
}

export function StocktakesPage() {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [starting, setStarting] = useState(false);
  const q = useQuery({ queryKey: ['stocktakes'], queryFn: () => api.get<StocktakeRow[]>('/api/stocktakes') });
  return (
    <>
      <PageHeader
        title="Stocktakes"
        description="Count what is on the shelves and correct the system. Differences are valued and recorded."
        actions={can('stock.stocktake') && <Button variant="primary" icon={<ClipboardList className="size-4" />} onClick={() => setStarting(true)}>Start a count</Button>}
      />
      <Panel>
        {q.isLoading && <Spinner />}
        {q.error && <div className="p-4"><ErrorNotice error={q.error} /></div>}
        {q.data?.length === 0 && <EmptyState title="No counts yet">Start with one category or the whole shop.</EmptyState>}
        {!!q.data?.length && (
          <ul className="divide-y divide-line">
            {q.data.map((s) => (
              <li key={s.id}>
                <Link to={`/stocktakes/${s.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 hover:bg-ledger-50/60">
                  <span>
                    <span className="font-medium">{s.number} — {s.name}</span>
                    <span className="block text-sm text-muted">
                      {s.branchName}, started {formatDateTime(s.startedAt)} — {s.countedCount} of {s.lineCount} counted
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    {s.varianceValueCents != null && (
                      <span className={clsx('num', s.varianceValueCents < 0 ? 'text-danger' : 'text-ok')}>{formatMoney(s.varianceValueCents, cur)}</span>
                    )}
                    <Badge tone={s.status === 'counting' ? 'gold' : s.status === 'posted' ? 'ok' : 'neutral'}>
                      {s.status === 'counting' ? 'Counting' : s.status === 'posted' ? 'Posted' : 'Cancelled'}
                    </Badge>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {starting && <StartDialog onClose={() => setStarting(false)} />}
    </>
  );
}

function StartDialog({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const nav = useNavigate();
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api.get<Category[]>('/api/categories') });
  const [branchId, setBranchId] = useState(branch?.id ?? '');
  const [categoryId, setCategoryId] = useState('');
  const [name, setName] = useState(`Count ${new Date().toLocaleDateString('en-GB')}`);
  const m = useMutation({
    mutationFn: () => api.post<{ id: string }>('/api/stocktakes', { branchId, categoryId: categoryId || null, name }),
    onSuccess: (s) => nav(`/stocktakes/${s.id}`),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title="Start a count"
      description="The system remembers what it expects right now. Count soon after starting; sales made while you count are handled correctly."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={m.isPending} onClick={() => m.mutate()}>Start counting</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="space-y-4">
        <Field label="Name" htmlFor="st-name">
          <TextInput id="st-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Branch" htmlFor="st-branch">
          <Select id="st-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="What to count" htmlFor="st-cat">
          <Select id="st-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Every stocked product</option>
            {cats.data?.map((c) => <option key={c.id} value={c.id}>{c.name} only</option>)}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}

export function StocktakeDetailPage() {
  const { id } = useParams();
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['stocktake', id], queryFn: () => api.get<StocktakeDetail>(`/api/stocktakes/${id}`) });
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [filter, setFilter] = useState('');
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [confirmPost, setConfirmPost] = useState(false);
  const dirty = Object.keys(counts).length > 0;

  const save = useMutation({
    mutationFn: () => api.put<StocktakeDetail>(`/api/stocktakes/${id}/counts`, { lines: Object.entries(counts).map(([productId, countedMilli]) => ({ productId, countedMilli })) }),
    onSuccess: (d) => {
      qc.setQueryData(['stocktake', id], d);
      setCounts({});
      toast('Counts saved');
    },
  });
  const post = useMutation({
    mutationFn: async () => {
      if (dirty) await save.mutateAsync();
      return api.post<StocktakeDetail>(`/api/stocktakes/${id}/post`);
    },
    onSuccess: (d) => {
      qc.setQueryData(['stocktake', id], d);
      void qc.invalidateQueries({ queryKey: ['stocktakes'] });
      void qc.invalidateQueries({ queryKey: ['products'] });
      setConfirmPost(false);
      toast('Count posted. Stock now matches what was counted.');
    },
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/api/stocktakes/${id}/cancel`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stocktake', id] });
      void qc.invalidateQueries({ queryKey: ['stocktakes'] });
    },
  });

  const st = q.data;
  const lines = useMemo(() => {
    if (!st) return [];
    const f = filter.trim().toLowerCase();
    return st.lines.filter((l) => {
      const counted = l.productId in counts ? counts[l.productId] : l.countedMilli;
      if (onlyDiff && (counted == null || counted === l.expectedMilli)) return false;
      return !f || l.name.toLowerCase().includes(f) || l.sku.toLowerCase().includes(f);
    });
  }, [st, filter, onlyDiff, counts]);

  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNotice error={q.error} />;
  if (!st) return null;
  const open = st.status === 'counting';
  const countedTotal = st.lines.filter((l) => (l.productId in counts ? counts[l.productId] : l.countedMilli) != null).length;

  return (
    <>
      <Link to="/stocktakes" className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-ledger-700 hover:underline">
        <ArrowLeft className="size-4" /> All counts
      </Link>
      <PageHeader
        title={`${st.number} — ${st.name}`}
        description={`${st.branchName}. ${countedTotal} of ${st.lines.length} products counted.${st.postedAt ? ` Posted ${formatDateTime(st.postedAt)}.` : ''}`}
        actions={
          open && (
            <>
              {can('stock.stocktake_approve') && <Button variant="ghost" className="text-danger" busy={cancel.isPending} onClick={() => cancel.mutate()}>Cancel count</Button>}
              <Button disabled={!dirty} busy={save.isPending} onClick={() => save.mutate()}>Save counts</Button>
              {can('stock.stocktake_approve') && <Button variant="primary" disabled={countedTotal === 0} onClick={() => setConfirmPost(true)}>Post count</Button>}
            </>
          )
        }
      />
      {st.status === 'posted' && st.varianceValueCents != null && (
        <p className={clsx('mb-5 rounded-[8px] px-4 py-3', st.varianceValueCents < 0 ? 'bg-danger-soft text-danger' : 'bg-ok-soft text-ok')}>
          Net difference at cost: <strong className="num">{formatMoney(st.varianceValueCents, cur)}</strong>
        </p>
      )}
      <FormError error={save.error ?? cancel.error} />
      <Panel>
        <div className="flex flex-wrap items-center gap-4 border-b border-line px-5 py-3">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <TextInput aria-label="Filter products" className="pl-9" placeholder="Find a product" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-[15px]">
            <input type="checkbox" className="size-4 accent-ledger-600" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} />
            Only show differences
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="border-b border-line text-sm text-muted">
                <th className="px-5 py-2.5 font-medium">Product</th>
                <th className="px-3 py-2.5 text-right font-medium">Expected</th>
                <th className="w-40 px-3 py-2.5 font-medium">Counted</th>
                <th className="px-3 py-2.5 text-right font-medium">Difference</th>
                {can('products.view_cost') && <th className="px-5 py-2.5 text-right font-medium">Value</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {lines.map((l) => {
                const counted = l.productId in counts ? counts[l.productId]! : l.countedMilli;
                const diff = counted != null ? counted - l.expectedMilli : null;
                return (
                  <tr key={l.productId}>
                    <td className="px-5 py-2">
                      {l.name}
                      <span className="block text-xs text-muted">{l.sku}{l.categoryName ? ` — ${l.categoryName}` : ''}</span>
                    </td>
                    <td className="px-3 py-2 text-right num">{formatQty(l.expectedMilli)}</td>
                    <td className="px-3 py-2">
                      {open ? (
                        <QtyInput unit={l.unit} allowZero value={counted} onChange={(v) => setCounts((c) => ({ ...c, [l.productId]: v }))} />
                      ) : (
                        <span className="num">{counted != null ? formatQty(counted) : 'Not counted'}</span>
                      )}
                    </td>
                    <td className={clsx('px-3 py-2 text-right num', diff != null && diff < 0 && 'text-danger', diff != null && diff > 0 && 'text-ok')}>
                      {diff == null ? '' : diff === 0 ? '—' : `${diff > 0 ? '+' : ''}${formatQty(diff)}`}
                    </td>
                    {can('products.view_cost') && (
                      <td className="px-5 py-2 text-right text-sm num">
                        {l.varianceValueCents != null && l.varianceValueCents !== 0 ? formatMoney(l.varianceValueCents, cur) : ''}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {lines.length === 0 && <EmptyState title="Nothing to show" />}
        </div>
      </Panel>
      {confirmPost && (
        <Dialog
          open
          onClose={() => setConfirmPost(false)}
          title="Post this count?"
          description="Stock for every counted product is corrected to what was counted. Products not counted are left as they are. This can't be undone."
          footer={
            <>
              <Button onClick={() => setConfirmPost(false)}>Keep counting</Button>
              <Button variant="primary" busy={post.isPending} onClick={() => post.mutate()}>Post count</Button>
            </>
          }
        >
          {post.error ? <ErrorNotice error={post.error} /> : <p className="text-muted">{countedTotal} products counted.</p>}
        </Dialog>
      )}
    </>
  );
}
